import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { budgetsAPI } from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { isDepartmentManager } from '../utils/roleHelpers';
import {
  ArrowLeft, Calendar, Download, RefreshCw, ChevronDown, ChevronRight,
  CreditCard, RotateCcw, AlertTriangle, FileText, PlayCircle,
  StopCircle, Info, X, Maximize2, Minimize2, Clock, CheckCircle,
  List, BarChart2, TrendingUp, TrendingDown, Edit3
} from 'lucide-react';

export default function BudgetMonthlyView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();

  const [budgets, setBudgets] = useState([]);
  const [selectedBudgetId, setSelectedBudgetId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [tooltipCell, setTooltipCell] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showValues, setShowValues] = useState('budgeted'); // budgeted | spent | committed | variance | forecast
  const [spreadMode, setSpreadMode] = useState('equal_spread'); // equal_spread | weighted | cashflow
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [statusFilter, setStatusFilter] = useState('Active');
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const [gridViewMode, setGridViewMode] = useState('grid'); // 'grid' | 'timeline'
  const gridRef = useRef(null);

  // Forecast edit state
  const [forecastEditLine, setForecastEditLine] = useState(null);
  const [forecastEditAmount, setForecastEditAmount] = useState('');
  const [forecastEditNotes, setForecastEditNotes] = useState('');
  const [forecastEditEffectiveDate, setForecastEditEffectiveDate] = useState('');
  const [forecastEditChangeType, setForecastEditChangeType] = useState('permanent');
  const [forecastSaving, setForecastSaving] = useState(false);

  // Committed PO drill-down
  const [committedLine, setCommittedLine] = useState(null);
  const [committedLoading, setCommittedLoading] = useState(false);

  const openCommittedModal = async (line) => {
    if (!line.committed || line.committed <= 0) return;
    setCommittedLoading(true);
    setCommittedLine({ line, pos: [], accruals: [] });
    try {
      const r = await budgetsAPI.getCommittedPOs(selectedBudgetId, line.budgetLineId);
      setCommittedLine({ line, pos: r.data?.pos || [], accruals: r.data?.accruals || [] });
    } catch (_) {}
    finally { setCommittedLoading(false); }
  };

  // Budgets filtered by the status dropdown
  const filteredBudgets = statusFilter === 'All' ? budgets : budgets.filter(b => b.Status === statusFilter);

  // If current selection drops out of filtered list, pick first available
  useEffect(() => {
    if (filteredBudgets.length === 0) return;
    if (!filteredBudgets.some(b => b.BudgetID === selectedBudgetId)) {
      setSelectedBudgetId(filteredBudgets[0].BudgetID);
    }
  }, [statusFilter, filteredBudgets]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group lines by GL Account
  const glGroups = useMemo(() => {
    if (!data?.lines?.length) return [];
    const groupMap = new Map();
    for (const line of data.lines) {
      const key = line.glAccountCode || 'UNASSIGNED';
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          glCode: key,
          glName: line.glAccountName || 'Unassigned',
          glType: line.glAccountType || '',
          lines: [],
        });
      }
      groupMap.get(key).lines.push(line);
    }
    // Compute subtotals per group
    return Array.from(groupMap.values()).map(group => {
      const subtotals = {
          annualBudget: group.lines.reduce((s, l) => s + l.annualBudget, 0),
          spent: group.lines.reduce((s, l) => s + l.spent, 0),
          committed: group.lines.reduce((s, l) => s + l.committed, 0),
          remaining: group.lines.reduce((s, l) => s + l.remaining, 0),
          forecastedAmount: group.lines.reduce((s, l) => s + (l.forecastedFYTotal ?? l.annualBudget), 0),
          months: data.months.map((_, mi) => ({
            budgeted: group.lines.reduce((s, l) => s + l.months[mi].budgeted, 0),
            spent: group.lines.reduce((s, l) => s + l.months[mi].spent, 0),
            accrued: group.lines.reduce((s, l) => s + l.months[mi].accrued, 0),
            variance: group.lines.reduce((s, l) => s + l.months[mi].variance, 0),
            forecasted: group.lines.reduce((s, l) => s + (l.months[mi].forecasted ?? 0), 0),
            forecastVsBudget: group.lines.reduce((s, l) => s + (l.months[mi].forecastVsBudget ?? 0), 0),
          })),
        };
      return { ...group, subtotals };
    });
  }, [data]);

  const toggleGroup = (glCode) => {
    setCollapsedGroups(prev => ({ ...prev, [glCode]: !prev[glCode] }));
  };

  // Flat ordered list of all cell keys (lineId-monthIdx) for range selection
  const allCellKeys = useMemo(() => {
    if (!data) return [];
    const keys = [];
    for (const group of glGroups) {
      for (const line of group.lines) {
        for (let mi = 0; mi < data.months.length; mi++) {
          keys.push(`${line.budgetLineId}-${mi}`);
        }
      }
    }
    return keys;
  }, [glGroups, data]);

  // Sum/count of selected cells
  const selectionSummary = useMemo(() => {
    if (selectedCells.size === 0 || !data) return null;
    let sum = 0;
    let count = 0;
    for (const group of glGroups) {
      for (const line of group.lines) {
        for (let mi = 0; mi < data.months.length; mi++) {
          const key = `${line.budgetLineId}-${mi}`;
          if (selectedCells.has(key)) {
            const m = line.months[mi];
            let val = m.budgeted;
            if (showValues === 'spent') val = m.spent;
            else if (showValues === 'committed') val = m.spent + m.accrued;
            else if (showValues === 'variance') val = m.variance;
            else if (showValues === 'forecast') val = m.forecasted ?? 0;
            sum += val;
            count++;
          }
        }
      }
    }
    return { sum, count };
  }, [selectedCells, data, glGroups, showValues]);

  // Forecast permissions
  const isFinanceAdmin = hasPermission('manage_budgets');
  const isDeptMgr = hasPermission('budgets.manage.my-departments');
  const canEditForecast = !!data && data.budget.status !== 'Closed' && (isFinanceAdmin || isDeptMgr);

  const openForecastEdit = (line) => {
    setForecastEditLine(line);
    setForecastEditAmount(line.forecastedAmount != null ? String(line.forecastedAmount) : String(line.annualBudget));
    setForecastEditNotes(line.forecastNotes || '');
    setForecastEditEffectiveDate(line.forecastEffectiveDate ? line.forecastEffectiveDate.split('T')[0] : '');
    setForecastEditChangeType(line.forecastChangeType || 'permanent');
  };

  const handleSaveForecast = async () => {
    if (!forecastEditLine || !selectedBudgetId) return;
    setForecastSaving(true);
    try {
      const amt = forecastEditAmount === '' ? null : parseFloat(forecastEditAmount);
      await budgetsAPI.updateForecast(selectedBudgetId, forecastEditLine.budgetLineId, {
        forecastedAmount: amt,
        forecastNotes: forecastEditNotes || null,
        forecastEffectiveDate: forecastEditEffectiveDate || null,
        forecastChangeType: forecastEditChangeType,
      });
      await fetchMonthlyData(selectedBudgetId, spreadMode);
      setForecastEditLine(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save forecast');
    } finally {
      setForecastSaving(false);
    }
  };

  // Load available budgets on mount
  useEffect(() => {
    fetchBudgets();
  }, []);

  // Fetch grid data when budget or spreadMode changes
  useEffect(() => {
    if (selectedBudgetId) {
      fetchMonthlyData(selectedBudgetId, spreadMode);
    }
  }, [selectedBudgetId, spreadMode]);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      let available = [];
      if (isDepartmentManager(user)) {
        const res = await budgetsAPI.getMyDepartmentBudgets();
        available = (res.data?.budgets || []).filter(b => b.BudgetID);
      } else {
        const res = await budgetsAPI.getAll({ page: 1, pageSize: 100 });
        available = (res.data.items || []).filter(b => b.BudgetID);
      }
      setBudgets(available);

      const budgetIdFromUrl = searchParams.get('budgetId');
      if (budgetIdFromUrl && available.some(b => b.BudgetID === budgetIdFromUrl)) {
        setSelectedBudgetId(budgetIdFromUrl);
      } else if (available.length > 0) {
        setSelectedBudgetId(available[0].BudgetID);
      }
    } catch (err) {
      console.error('Failed to fetch budgets:', err);
      setError('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyData = async (budgetId, viewMode = 'equal_spread') => {
    try {
      setLoadingGrid(true);
      setError(null);
      const res = await budgetsAPI.getMonthlyView(budgetId, viewMode);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load monthly view:', err);
      setError(err.response?.data?.message || 'Failed to load monthly budget view');
      setData(null);
    } finally {
      setLoadingGrid(false);
    }
  };

  const toggleRow = (lineId) => {
    setExpandedRows(prev => ({ ...prev, [lineId]: !prev[lineId] }));
  };

  const formatCurrency = (val) => {
    if (val == null || isNaN(val)) return '—';
    return '£' + Number(val).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatCurrencyFull = (val) => {
    if (val == null || isNaN(val)) return '—';
    return '£' + Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Cell color based on variance
  const getCellBg = (monthData) => {
    if (showValues === 'forecast') {
      const diff = (monthData.forecastVsBudget ?? 0);
      if (diff > 0) return 'bg-purple-50 text-purple-800';      // forecast exceeds budget
      if (diff < 0) return 'bg-teal-50 text-teal-700';          // forecast under budget
      return 'bg-purple-50/40 text-purple-700';
    }
    if (showValues === 'variance') {
      if (monthData.variance < 0) return 'bg-red-50 text-red-700';
      if (monthData.spent > 0 && monthData.variance >= 0) return 'bg-green-50 text-green-700';
    }
    if (showValues === 'spent' && monthData.spent > 0) {
      return monthData.spent > monthData.budgeted ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700';
    }
    if (showValues === 'committed') {
      const total = monthData.spent + monthData.accrued;
      if (total > 0) return total > monthData.budgeted ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700';
    }
    return '';
  };

  const getCellValue = (monthData) => {
    switch (showValues) {
      case 'spent': return monthData.spent;
      case 'committed': return monthData.spent + monthData.accrued;
      case 'variance': return monthData.variance;
      case 'forecast': return monthData.forecasted ?? 0;
      default: return monthData.budgeted;
    }
  };

  // Event icons
  const EventIcon = ({ event }) => {
    const baseClass = 'w-3.5 h-3.5 flex-shrink-0';
    switch (event.type) {
      case 'payment_due':
        return <CreditCard className={`${baseClass} text-blue-500`} title={event.label} />;
      case 'renewal':
        return <RotateCcw className={`${baseClass} text-amber-500`} title={event.label} />;
      case 'contract_end':
        return <StopCircle className={`${baseClass} text-red-500`} title={event.label} />;
      case 'contract_start':
        return <PlayCircle className={`${baseClass} text-green-500`} title={event.label} />;
      default:
        return null;
    }
  };

  // Export to CSV
  const exportCSV = () => {
    if (!data) return;
    const headers = ['GL Code', 'Description', 'Annual Budget', ...data.months.map(m => m.label), 'Total Spent', 'Remaining'];
    const rows = data.lines.map(line => [
      line.glAccountCode || '',
      line.description || line.glAccountName || '',
      line.annualBudget,
      ...line.months.map(m => m.budgeted.toFixed(2)),
      line.spent,
      line.remaining
    ]);
    rows.push(['TOTAL', '', data.summary.totalAnnualBudget, ...data.monthTotals.map(t => t.budgeted.toFixed(2)), data.summary.totalSpent, data.summary.totalRemaining]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `budget-monthly-${data.budget.budgetName || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Fullscreen toggle
  const toggleFullscreen = () => setIsFullscreen(prev => !prev);

  // Cell click: single, Ctrl+click (toggle), Shift+click (range)
  const handleCellClick = (e, cellId) => {
    if (e.shiftKey && selectionAnchor) {
      const anchorIdx = allCellKeys.indexOf(selectionAnchor);
      const clickIdx = allCellKeys.indexOf(cellId);
      const start = Math.min(anchorIdx, clickIdx);
      const end = Math.max(anchorIdx, clickIdx);
      setSelectedCells(new Set(allCellKeys.slice(start, end + 1)));
    } else if (e.ctrlKey || e.metaKey) {
      setSelectedCells(prev => {
        const next = new Set(prev);
        if (next.has(cellId)) next.delete(cellId); else next.add(cellId);
        return next;
      });
      setSelectionAnchor(cellId);
    } else {
      setSelectedCells(new Set([cellId]));
      setSelectionAnchor(cellId);
    }
  };

  // Escape clears selection
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedCells(new Set()); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading budgets…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white overflow-auto' : 'p-4 md:p-6'}`}>
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="btn-ghost-primary" title="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Monthly Budget View
            </h1>
            {data && (
              <p className="text-sm text-gray-500 mt-0.5">
                {data.budget.departmentName && <span className="font-medium">{data.budget.departmentName}</span>}
                {data.budget.departmentName && ' · '}
                {data.budget.yearLabel || data.budget.financialYear}
                {' · '}{data.totalWeeks} weeks · {data.summary.lineCount} lines
                {' · '}<span className="text-indigo-600 font-medium">
                  {spreadMode === 'weighted' ? 'Weighted spread' : spreadMode === 'cashflow' ? 'Cashflow view' : 'Equal spread'}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input text-sm w-32"
            title="Filter budgets by status"
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Draft">Draft</option>
            <option value="Closed">Closed</option>
          </select>

          {/* Budget selector */}
          <select
            value={selectedBudgetId || ''}
            onChange={(e) => setSelectedBudgetId(e.target.value)}
            className="input text-sm max-w-[280px]"
          >
            {filteredBudgets.map(b => (
              <option key={b.BudgetID} value={b.BudgetID}>
                {b.BudgetName}{b.BudgetType && b.BudgetType !== 'Department' ? ` [${b.BudgetType}]` : ''}{b.DepartmentName ? ` (${b.DepartmentName})` : ''}
              </option>
            ))}
            {filteredBudgets.length === 0 && (
              <option disabled value="">No budgets for this status</option>
            )}
          </select>

          {/* Value toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
            {[
              { key: 'budgeted',  label: 'Budget',    tip: 'Monthly allocated budget\n= Annual budget ÷ 12 (or weighted by weeks)' },
              { key: 'spent',     label: 'Actual',    tip: 'Confirmed spend only\n= Approved & posted invoices' },
              { key: 'committed', label: 'Committed', tip: 'Total obligation including future costs\n= Actual invoices + Active accruals' },
              { key: 'variance',  label: 'Variance',  tip: 'Budget headroom (positive = under budget)\n= Budget − Actual spend' },
              { key: 'forecast',  label: 'Forecast',  tip: 'Latest Estimate (LE) — revised annual projection\nPast months = actuals · Future months = (Forecast − YTD) ÷ remaining months' },
            ].map(v => (
              <button
                key={v.key}
                onClick={() => setShowValues(v.key)}
                title={v.tip}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  showValues === v.key
                    ? v.key === 'committed' ? 'bg-amber-500 text-white'
                      : v.key === 'forecast' ? 'bg-violet-600 text-white'
                      : 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          {/* Spread mode toggle */}
          <div className="flex rounded-lg border border-indigo-300 overflow-hidden text-xs" title="Budget spread view mode">
            {[
              { key: 'equal_spread', label: 'Equal', tip: 'Equal split weighted by weeks per month' },
              { key: 'weighted',     label: 'Weighted', tip: 'Custom weighted monthly spread (heavier in certain months)' },
              { key: 'cashflow',     label: 'Cashflow', tip: 'Actual cash outflows by invoice date only' },
            ].map(v => (
              <button
                key={v.key}
                onClick={() => setSpreadMode(v.key)}
                title={v.tip}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  spreadMode === v.key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          {/* Grid / Timeline view toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
            <button
              onClick={() => setGridViewMode('grid')}
              className={`flex items-center gap-1 px-3 py-1.5 font-medium transition-colors ${
                gridViewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="Spreadsheet view — detailed numbers"
            >
              <List className="w-3.5 h-3.5" /> Grid
            </button>
            <button
              onClick={() => setGridViewMode('timeline')}
              className={`flex items-center gap-1 px-3 py-1.5 font-medium transition-colors ${
                gridViewMode === 'timeline' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="Timeline bar view — visual spend vs budget"
            >
              <BarChart2 className="w-3.5 h-3.5" /> Timeline
            </button>
          </div>

          <button onClick={exportCSV} className="btn btn-secondary btn-sm" disabled={!data} title="Export CSV">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button onClick={toggleFullscreen} className="btn btn-secondary btn-sm" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button onClick={() => selectedBudgetId && fetchMonthlyData(selectedBudgetId, spreadMode)} className="btn btn-secondary btn-sm" title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loadingGrid ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Summary Bar ── */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          {(() => {
            const remaining = data.summary.totalRemaining;
            const remainingColor = remaining < 0 ? 'red' : 'emerald';
            const cards = [
              { label: 'Annual Budget', value: formatCurrencyFull(data.summary.totalAnnualBudget), icon: Calendar, color: 'blue' },
              { label: 'Actual Spent', value: formatCurrencyFull(data.summary.totalSpent), icon: CreditCard, color: 'green' },
              { label: 'Accrued', value: formatCurrencyFull(data.summary.totalAccrued), icon: Clock, color: 'amber' },
              { label: 'Committed', value: formatCurrencyFull(data.summary.totalCommitted || 0), icon: AlertTriangle, color: 'orange' },
              { label: 'Remaining', value: formatCurrencyFull(remaining), icon: CheckCircle, color: remainingColor },
              { label: 'Budget Status', value: data.budget.status, icon: FileText, color: 'gray', badge: true },
            ];
            const borderColor = { blue: 'border-blue-500', green: 'border-green-500', amber: 'border-amber-500', orange: 'border-orange-500', red: 'border-red-500', emerald: 'border-emerald-500', gray: 'border-gray-400' };
            const iconColor = { blue: 'text-blue-500', green: 'text-green-500', amber: 'text-amber-500', orange: 'text-orange-500', red: 'text-red-500', emerald: 'text-emerald-500', gray: 'text-gray-400' };
            return cards.map((card, i) => {
              const Icon = card.icon;
              return (
                <div key={i} className={`bg-white rounded-lg shadow px-3 py-3 border-l-4 ${borderColor[card.color] || 'border-gray-400'}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
                      {card.badge ? (
                        <span className={`inline-flex mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${
                          data.budget.status === 'Active' ? 'bg-green-100 text-green-800'
                          : data.budget.status === 'Draft' ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                        }`}>{card.value}</span>
                      ) : (
                        <p className="text-base font-bold text-gray-900 mt-0.5 truncate">{card.value}</p>
                      )}
                    </div>
                    <Icon className={`w-6 h-6 flex-shrink-0 ml-2 ${iconColor[card.color] || 'text-gray-400'}`} />
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ── Forecast Banner (only in Forecast view) ── */}
      {showValues === 'forecast' && data && (() => {
        const totalForecast = data.summary.totalForecast ?? data.summary.totalAnnualBudget;
        const forecastVariance = data.summary.totalForecastVariance ?? 0;
        const pctChange = data.summary.totalAnnualBudget > 0
          ? ((forecastVariance / data.summary.totalAnnualBudget) * 100).toFixed(1)
          : '0.0';
        const isOver = forecastVariance > 0;
        return (
          <div className={`flex flex-wrap items-center gap-4 mb-3 px-4 py-2.5 rounded-lg border text-sm ${isOver ? 'bg-purple-50 border-purple-200' : 'bg-teal-50 border-teal-200'}`}>
            <TrendingUp className={`w-4 h-4 flex-shrink-0 ${isOver ? 'text-purple-600' : 'text-teal-600'}`} />
            <div>
              <span className="font-semibold text-gray-700">Latest Estimate (LE): </span>
              <span className={`font-bold ${isOver ? 'text-purple-700' : 'text-teal-700'}`}>{formatCurrencyFull(totalForecast)}</span>
            </div>
            <div className={`flex items-center gap-1 font-medium ${isOver ? 'text-purple-600' : 'text-teal-600'}`}>
              {isOver ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              {isOver ? '+' : ''}{formatCurrencyFull(forecastVariance)} vs OB ({isOver ? '+' : ''}{pctChange}%)
            </div>
            <div className="text-xs text-gray-400 ml-auto">
              Past months = actuals · Future months = projected from revised annual forecast
            </div>
            {canEditForecast && (
              <div className="text-xs text-violet-600 font-medium">Click ✎ on a line to revise forecast</div>
            )}
          </div>
        );
      })()}

      {/* ── Legend ── */}
      {data && (
        <div className="flex flex-wrap items-center gap-4 mb-3 text-xs text-gray-500 px-1">
          <span className="flex items-center gap-1"><PlayCircle className="w-3.5 h-3.5 text-green-500" /> Contract Start</span>
          <span className="flex items-center gap-1"><StopCircle className="w-3.5 h-3.5 text-red-500" /> Contract End</span>
          <span className="flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5 text-amber-500" /> Renewal</span>
          <span className="flex items-center gap-1"><CreditCard className="w-3.5 h-3.5 text-blue-500" /> Payment Due</span>
          <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-indigo-500" /> Invoice(s)</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-amber-500" /> Accrual (awaiting invoice)</span>
        </div>
      )}

      {/* ── Grid ── */}
      {loadingGrid ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
        </div>
      ) : data && data.lines.length > 0 ? (
        <>
        {gridViewMode === 'grid' && <div ref={gridRef} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-auto max-h-[calc(100vh-280px)]">
          <table className="min-w-max w-full border-collapse text-xs">
            {/* Header */}
            <thead className="sticky top-0 z-20">
              <tr className="bg-gray-800 text-white">
                <th className="sticky left-0 z-30 bg-gray-800 px-2 py-2.5 text-left font-semibold min-w-[30px]"></th>
                <th className="sticky left-[30px] z-30 bg-gray-800 px-3 py-2.5 text-left font-semibold min-w-[80px]">GL Code</th>
                <th className="sticky left-[110px] z-30 bg-gray-800 px-3 py-2.5 text-left font-semibold min-w-[180px] max-w-[240px]">Description</th>
                <th className="px-3 py-2.5 text-right font-semibold min-w-[90px] bg-gray-700">
                  {showValues === 'forecast' ? 'Forecast LE' : 'Annual'}
                </th>
                {data.months.map((m, i) => (
                  <th key={i} className="px-2 py-2.5 text-center font-semibold min-w-[85px] border-l border-gray-700" title={`${m.fullLabel} (${m.weeks} wks)`}>
                    <div>{m.label}</div>
                    <div className="text-[10px] font-normal opacity-70">{m.weeks}w</div>
                  </th>
                ))}
                <th className="px-3 py-2.5 text-right font-semibold min-w-[80px] bg-gray-700 border-l border-gray-700">
                  {showValues === 'committed' ? 'Committed' : 'Spent'}
                </th>
                <th className="px-3 py-2.5 text-right font-semibold min-w-[80px] bg-gray-700">Remain</th>
              </tr>
            </thead>

            {/* Body — grouped by GL Account */}
            <tbody>
              {glGroups.map((group, groupIdx) => {
                const isGroupCollapsed = collapsedGroups[group.glCode];
                const lineCount = group.lines.length;
                const showGrouping = glGroups.length > 1 || lineCount > 1;

                return (
                  <React.Fragment key={group.glCode}>
                    {/* ── GL Group Header ── */}
                    {showGrouping && (
                      <tr className={`bg-slate-100 border-t-2 border-slate-300 ${groupIdx > 0 ? 'border-t-2' : ''}`}>
                        <td className="sticky left-0 z-10 bg-slate-100 px-2 py-2 text-center">
                          <button onClick={() => toggleGroup(group.glCode)} className="text-slate-500 hover:text-slate-700">
                            {isGroupCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="sticky left-[30px] z-10 bg-slate-100 px-3 py-2" colSpan={2}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-slate-700 text-xs">{group.glCode}</span>
                            <span className="text-slate-500 text-[10px]">—</span>
                            <span className="font-semibold text-slate-600 text-xs truncate">{group.glName}</span>
                            {group.glType && <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-medium">{group.glType}</span>}
                            <span className="text-[10px] text-slate-400 ml-1">({lineCount} {lineCount === 1 ? 'line' : 'lines'})</span>
                          </div>
                        </td>
                        <td className="bg-slate-100 px-3 py-2 text-right font-mono font-bold text-slate-700 text-xs">
                          {formatCurrency(showValues === 'forecast' ? group.subtotals.forecastedAmount : group.subtotals.annualBudget)}
                        </td>
                        {group.subtotals.months.map((sm, mi) => (
                          <td key={mi} className="bg-slate-100 px-2 py-2 text-right font-mono text-slate-500 text-xs border-l border-slate-200">
                            {formatCurrency(
                              showValues === 'spent' ? sm.spent
                              : showValues === 'committed' ? sm.spent + sm.accrued
                              : showValues === 'variance' ? sm.variance
                              : showValues === 'forecast' ? (sm.forecasted ?? 0)
                              : sm.budgeted
                            )}
                          </td>
                        ))}
                        <td className="bg-slate-100 px-3 py-2 text-right font-mono font-bold text-slate-700 text-xs border-l border-slate-200">
                          {formatCurrency(group.subtotals.spent)}
                        </td>
                        <td className="bg-slate-100 px-3 py-2 text-right font-mono font-bold text-xs">
                          <span className={group.subtotals.remaining < 0 ? 'text-red-600' : 'text-green-700'}>
                            {formatCurrency(group.subtotals.remaining)}
                          </span>
                        </td>
                      </tr>
                    )}

                    {/* ── Lines within GL Group ── */}
                    {!isGroupCollapsed && group.lines.map((line, rowIdx) => {
                      const isExpanded = expandedRows[line.budgetLineId];
                      const hasContract = !!line.contract;
                      const hasEvents = line.months.some(m => m.events.length > 0);

                      return (
                        <React.Fragment key={line.budgetLineId}>
                          {/* Main row */}
                          <tr className={`border-b border-gray-100 hover:bg-blue-50 transition-colors group ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            {/* Expand toggle */}
                            <td className={`sticky left-0 z-10 px-2 py-2 text-center group-hover:bg-blue-50 transition-colors ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              {(hasContract || hasEvents) && (
                                <button onClick={() => toggleRow(line.budgetLineId)} className="text-gray-400 hover:text-gray-600">
                                  {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </td>

                            {/* GL Code — dimmed since group header shows it */}
                            <td className={`sticky left-[30px] z-10 px-3 py-2 font-mono text-gray-400 text-[10px] group-hover:bg-blue-50 transition-colors ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              {showGrouping ? '' : (line.glAccountCode || '—')}
                            </td>

                            {/* Description */}
                            <td className={`sticky left-[110px] z-10 px-3 py-2 max-w-[240px] group-hover:bg-blue-50 transition-colors ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              <div className="truncate font-medium text-gray-900" title={line.description || line.glAccountName}>
                                {line.description || line.glAccountName || '—'}
                              </div>
                              <div className="flex items-center gap-1 mt-0.5">
                                {line.lineType && line.lineType !== 'one_off' && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    line.lineType === 'new_service' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {line.lineType === 'new_service' ? 'New Svc' : 'Continuity'}
                                  </span>
                                )}
                                {line.spreadMonths > 0 && (
                                  <span className="text-[10px] text-gray-400">{line.spreadMonths}m</span>
                                )}
                                {line.isCarryOver && (
                                  <span className="text-[10px] text-indigo-500">↩</span>
                                )}
                                {hasContract && (
                                  <div className="truncate text-[10px] text-gray-400" title={`${line.contract.vendor} — ${line.contract.name}`}>
                                    🔗 {line.contract.vendor} · {line.contract.frequency}
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Annual / Forecast LE */}
                            <td className="px-3 py-2 text-right font-mono font-semibold text-gray-800 bg-gray-50/80">
                              {showValues === 'forecast' ? (
                                <div className="flex items-center justify-end gap-1">
                                  <span className={line.forecastVarianceAnnual > 0 ? 'text-purple-700' : line.forecastVarianceAnnual < 0 ? 'text-teal-700' : 'text-gray-800'}>
                                    {formatCurrency(line.forecastedFYTotal ?? line.annualBudget)}
                                  </span>
                                  {canEditForecast && (
                                    <button
                                      onClick={() => openForecastEdit(line)}
                                      className="ml-1 text-violet-400 hover:text-violet-700 transition-colors"
                                      title="Edit forecast"
                                    >
                                      <Edit3 className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              ) : (
                                formatCurrency(line.annualBudget)
                              )}
                            </td>

                            {/* Month cells */}
                            {line.months.map((m, mi) => {
                              const cellVal = getCellValue(m);
                              const hasInvoices = m.invoiceCount > 0;
                              const hasEvts = m.events.length > 0;
                              const hasAccruals = m.accrualCount > 0;
                              const cellId = `${line.budgetLineId}-${mi}`;
                              const hasTooltip = hasInvoices || hasEvts || hasAccruals;

                              return (
                                <td
                                  key={mi}
                                  className={`px-2 py-2 text-right font-mono border-l border-gray-100 relative cursor-pointer select-none ${selectedCells.has(cellId) ? 'ring-2 ring-inset ring-blue-500 bg-blue-100/60' : getCellBg(m)}`}
                                  onClick={(e) => handleCellClick(e, cellId)}
                                  onMouseEnter={(e) => { if (hasTooltip) { setTooltipPos({ x: e.clientX, y: e.clientY }); setTooltipCell(cellId); } }}
                                  onMouseMove={(e) => { if (hasTooltip) setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                                  onMouseLeave={() => setTooltipCell(null)}
                                >
                                  <div className="flex items-center justify-end gap-1">
                                    {hasEvts && (
                                      <span className="flex gap-0.5">
                                        {m.events.map((ev, ei) => <EventIcon key={ei} event={ev} />)}
                                      </span>
                                    )}
                                    {hasInvoices && <FileText className="w-3 h-3 text-indigo-400 flex-shrink-0" />}
                                    {hasAccruals && <Clock className="w-3 h-3 text-amber-400 flex-shrink-0" />}
                                    <span>{formatCurrency(cellVal)}</span>
                                  </div>
                                  {/* Accrual sub-line: show in spent view when accruals exist */}
                                  {showValues === 'spent' && hasAccruals && (
                                    <div className="text-[10px] text-amber-600 text-right leading-tight mt-0.5">
                                      +{formatCurrency(m.accrued)} accrued
                                    </div>
                                  )}

                                  {/* Tooltip */}
                                  {tooltipCell === cellId && hasTooltip && (
                                    <div
                                      className="fixed z-[9999] w-64 bg-gray-900 text-white text-[11px] rounded-lg shadow-xl p-3 pointer-events-none"
                                      style={{
                                        top: tooltipPos.y - 12,
                                        left: Math.min(tooltipPos.x + 12, window.innerWidth - 268),
                                        transform: 'translateY(-100%)'
                                      }}
                                    >
                                      <div className="font-semibold mb-1">{m.fullLabel}</div>
                                      <div className="text-gray-300">Budget: {formatCurrencyFull(m.budgeted)}</div>
                                      {m.spent > 0 && <div className="text-blue-300">Actual: {formatCurrencyFull(m.spent)}</div>}
                                      {m.accrued > 0 && <div className="text-amber-300">Accrued: {formatCurrencyFull(m.accrued)}</div>}
                                      {m.accrued > 0 && m.spent > 0 && (
                                        <div className="text-amber-200 font-semibold">Committed: {formatCurrencyFull(m.spent + m.accrued)}</div>
                                      )}
                                      {m.variance !== m.budgeted && (
                                        <div className={m.variance < 0 ? 'text-red-300' : 'text-green-300'}>
                                          Variance: {formatCurrencyFull(m.variance)}
                                        </div>
                                      )}
                                      {m.forecasted != null && m.forecasted !== m.budgeted && (
                                        <div className="text-violet-300">
                                          Forecast: {formatCurrencyFull(m.forecasted)}
                                        </div>
                                      )}
                                      {hasAccruals && (
                                        <div className="mt-1.5 pt-1 border-t border-gray-700 space-y-0.5">
                                          <div className="text-amber-300 font-semibold flex items-center gap-1">
                                            <Clock className="w-3 h-3" /> Accruals
                                          </div>
                                          {m.accrualItems.slice(0, 4).map((ac, ai) => (
                                            <div key={ai} className="flex justify-between gap-2">
                                              <span className="truncate text-gray-300">{ac.vendorName || ac.description || 'Accrual'}</span>
                                              <span className="text-amber-300 flex-shrink-0">{formatCurrencyFull(ac.amount)}</span>
                                            </div>
                                          ))}
                                          {m.accrualCount > 4 && <div className="text-gray-400">+{m.accrualCount - 4} more</div>}
                                        </div>
                                      )}
                                      {hasEvts && (
                                        <div className="mt-1.5 pt-1 border-t border-gray-700 space-y-0.5">
                                          {m.events.map((ev, ei) => (
                                            <div key={ei} className="flex items-center gap-1">
                                              <EventIcon event={ev} />
                                              <span>{ev.label}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {hasInvoices && (
                                        <div className="mt-1.5 pt-1 border-t border-gray-700 space-y-0.5">
                                          {m.invoices.slice(0, 5).map((inv, ii) => (
                                            <div key={ii} className="flex justify-between">
                                              <span className="truncate mr-2">{inv.invoiceNumber || 'Invoice'}</span>
                                              <span>{formatCurrencyFull(inv.amount)}</span>
                                            </div>
                                          ))}
                                          {m.invoiceCount > 5 && <div className="text-gray-400">+{m.invoiceCount - 5} more</div>}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                              );
                            })}

                            {/* Spent total / Committed (clickable in committed mode) */}
                            <td className="px-3 py-2 text-right font-mono bg-gray-50/80 border-l border-gray-200">
                              {showValues === 'committed' ? (
                                line.committed > 0 ? (
                                  <button
                                    onClick={() => openCommittedModal(line)}
                                    className="text-amber-600 hover:text-amber-800 hover:underline font-semibold"
                                    title="View committed POs"
                                  >
                                    {formatCurrency(line.committed)}
                                  </button>
                                ) : (
                                  <span className="text-gray-400">{formatCurrency(0)}</span>
                                )
                              ) : (
                                <span className={line.spent > line.annualBudget ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                                  {formatCurrency(line.spent)}
                                </span>
                              )}
                            </td>

                            {/* Remaining */}
                            <td className="px-3 py-2 text-right font-mono bg-gray-50/80">
                              <span className={line.remaining < 0 ? 'text-red-600 font-semibold' : 'text-green-700'}>
                                {formatCurrency(line.remaining)}
                              </span>
                            </td>
                          </tr>

                          {/* Expanded detail row */}
                          {isExpanded && (
                            <tr className="bg-blue-50/30">
                              <td colSpan={4 + data.months.length + 2} className="px-6 py-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                  {/* GL & Dimensions */}
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Details</h4>
                                    <div className="space-y-0.5 text-gray-600">
                                      <div>GL: <span className="font-medium">{line.glAccountCode}</span> — {line.glAccountName}</div>
                                      {line.departmentName && <div>Department: <span className="font-medium">{line.departmentName}</span></div>}
                                      {line.projectName && <div>Project: <span className="font-medium">{line.projectCode} — {line.projectName}</span></div>}
                                      {line.costCentre && <div>Cost Centre: <span className="font-medium">{line.costCentre}</span></div>}
                                    </div>
                                  </div>

                                  {/* Contract info */}
                                  {hasContract && (
                                    <div>
                                      <h4 className="font-semibold text-gray-700 mb-1">Contract</h4>
                                      <div className="space-y-0.5 text-gray-600">
                                        <div>Name: <span className="font-medium">{line.contract.name}</span></div>
                                        <div>Vendor: <span className="font-medium">{line.contract.vendor}</span></div>
                                        <div>Frequency: <span className="font-medium">{line.contract.frequency}</span></div>
                                        <div>Payment: <span className="font-medium">{formatCurrencyFull(line.contract.paymentAmount)}</span></div>
                                        {line.contract.number && <div>Contract #: <span className="font-medium">{line.contract.number}</span></div>}
                                      </div>
                                    </div>
                                  )}

                                  {/* Dates */}
                                  {hasContract && (
                                    <div>
                                      <h4 className="font-semibold text-gray-700 mb-1">Key Dates</h4>
                                      <div className="space-y-0.5 text-gray-600">
                                        {line.contract.startDate && (
                                          <div className="flex items-center gap-1">
                                            <PlayCircle className="w-3 h-3 text-green-500" />
                                            Start: {new Date(line.contract.startDate).toLocaleDateString('en-GB')}
                                          </div>
                                        )}
                                        {line.contract.endDate && (
                                          <div className="flex items-center gap-1">
                                            <StopCircle className="w-3 h-3 text-red-500" />
                                            End: {new Date(line.contract.endDate).toLocaleDateString('en-GB')}
                                          </div>
                                        )}
                                        {line.contract.renewalDate && (
                                          <div className="flex items-center gap-1">
                                            <RotateCcw className="w-3 h-3 text-amber-500" />
                                            Renewal: {new Date(line.contract.renewalDate).toLocaleDateString('en-GB')}
                                            {line.contract.autoRenew && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1 rounded">Auto</span>}
                                          </div>
                                        )}
                                        <div>Status: <span className={`font-medium ${
                                          line.contract.status === 'Active' ? 'text-green-600' : 'text-red-600'
                                        }`}>{line.contract.status}</span></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {/* ── GL Subtotal Row ── */}
                    {showGrouping && !isGroupCollapsed && lineCount > 1 && (
                      <tr className="bg-amber-50 border-t-2 border-amber-300 border-b-2 border-b-amber-200">
                        <td className="sticky left-0 z-10 bg-amber-50 px-2 py-2"></td>
                        <td className="sticky left-[30px] z-10 bg-amber-50 px-3 py-2" colSpan={2}>
                          <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">Subtotal — {group.glCode} {group.glName}</span>
                        </td>
                        <td className="bg-amber-50 px-3 py-2 text-right font-mono font-extrabold text-amber-900 text-sm">
                          {formatCurrency(showValues === 'forecast' ? group.subtotals.forecastedAmount : group.subtotals.annualBudget)}
                        </td>
                        {group.subtotals.months.map((sm, mi) => (
                          <td key={mi} className="bg-amber-50 px-2 py-2 text-right font-mono font-bold text-amber-800 text-xs border-l border-amber-200">
                            {formatCurrency(
                              showValues === 'spent' ? sm.spent
                              : showValues === 'committed' ? sm.spent + sm.accrued
                              : showValues === 'variance' ? sm.variance
                              : showValues === 'forecast' ? (sm.forecasted ?? 0)
                              : sm.budgeted
                            )}
                          </td>
                        ))}
                        <td className="bg-amber-50 px-3 py-2 text-right font-mono font-extrabold text-amber-900 text-xs border-l border-amber-200">
                          {formatCurrency(group.subtotals.spent)}
                        </td>
                        <td className="bg-amber-50 px-3 py-2 text-right font-mono font-extrabold text-xs">
                          <span className={group.subtotals.remaining < 0 ? 'text-red-700 font-extrabold' : 'text-green-700 font-extrabold'}>
                            {formatCurrency(group.subtotals.remaining)}
                          </span>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>

            {/* Footer totals */}
            <tfoot className="sticky bottom-0 z-20">
              <tr className="bg-gray-800 text-white font-semibold">
                <td className="sticky left-0 z-30 bg-gray-800 px-2 py-2.5"></td>
                <td className="sticky left-[30px] z-30 bg-gray-800 px-3 py-2.5" colSpan={2}>TOTAL</td>
                <td className="px-3 py-2.5 text-right font-mono bg-gray-700">
                  {formatCurrency(showValues === 'forecast'
                    ? (data.summary.totalForecast ?? data.summary.totalAnnualBudget)
                    : data.summary.totalAnnualBudget)}
                </td>
                {data.monthTotals.map((t, i) => (
                  <td key={i} className="px-2 py-2.5 text-right font-mono border-l border-gray-700">
                    {showValues === 'spent' ? formatCurrency(t.spent)
                     : showValues === 'committed' ? formatCurrency(t.spent + t.accrued)
                     : showValues === 'variance' ? formatCurrency(t.variance)
                     : showValues === 'forecast' ? formatCurrency(t.forecasted ?? 0)
                     : formatCurrency(t.budgeted)}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right font-mono bg-gray-700 border-l border-gray-700">
                  {formatCurrency(data.summary.totalSpent)}
                </td>
                <td className="px-3 py-2.5 text-right font-mono bg-gray-700">
                  {formatCurrency(data.summary.totalRemaining)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>} {/* end gridViewMode === 'grid' */}

        {/* ── Timeline Bar View ── */}
        {gridViewMode === 'timeline' && (
          <div ref={gridRef} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-auto max-h-[calc(100vh-280px)]">
            {/* Legend */}
            <div className="sticky top-0 z-30 flex flex-wrap items-center gap-5 px-4 py-2 bg-gray-50 border-b border-gray-200 text-[11px] text-gray-500">
              <span className="font-semibold text-gray-600">Spend vs Budget:</span>
              <span className="flex items-center gap-1.5"><span className="w-10 h-2 rounded-full bg-blue-500 inline-block" /> &lt;80%</span>
              <span className="flex items-center gap-1.5"><span className="w-10 h-2 rounded-full bg-amber-500 inline-block" /> 80–100%</span>
              <span className="flex items-center gap-1.5"><span className="w-10 h-2 rounded-full bg-red-500 inline-block" /> Over budget</span>
              <span className="flex items-center gap-1.5"><PlayCircle className="w-3 h-3 text-green-500" /> Start</span>
              <span className="flex items-center gap-1.5"><StopCircle className="w-3 h-3 text-red-500" /> End</span>
              <span className="flex items-center gap-1.5"><RotateCcw className="w-3 h-3 text-amber-500" /> Renewal</span>
              <span className="flex items-center gap-1.5"><CreditCard className="w-3 h-3 text-blue-500" /> Payment</span>
            </div>
            <table className="min-w-max w-full border-collapse text-xs">
              <thead className="sticky top-[33px] z-20">
                <tr className="bg-gray-800 text-white">
                  <th className="sticky left-0 z-30 bg-gray-800 px-2 py-2.5 min-w-[30px]"></th>
                  <th className="sticky left-[30px] z-30 bg-gray-800 px-3 py-2.5 text-left font-semibold min-w-[80px]">GL Code</th>
                  <th className="sticky left-[110px] z-30 bg-gray-800 px-3 py-2.5 text-left font-semibold min-w-[180px] max-w-[240px]">Description</th>
                  <th className="px-3 py-2.5 text-right font-semibold min-w-[90px] bg-gray-700">Annual</th>
                  {data.months.map((m, i) => (
                    <th key={i} className="px-2 py-2.5 text-center font-semibold min-w-[90px] border-l border-gray-700" title={m.fullLabel}>
                      <div>{m.label}</div>
                      <div className="text-[10px] font-normal opacity-70">{m.weeks}w</div>
                    </th>
                  ))}
                  <th className="px-3 py-2.5 text-right font-semibold min-w-[70px] bg-gray-700 border-l border-gray-700">FY %</th>
                </tr>
              </thead>
              <tbody>
                {glGroups.map((group) => {
                  const isGroupCollapsed = collapsedGroups[group.glCode];
                  const lineCount = group.lines.length;
                  const showGrouping = glGroups.length > 1 || lineCount > 1;
                  const groupPct = group.subtotals.annualBudget > 0
                    ? Math.round((group.subtotals.spent / group.subtotals.annualBudget) * 100) : 0;

                  return (
                    <React.Fragment key={group.glCode}>
                      {showGrouping && (
                        <tr className="bg-slate-100 border-t-2 border-slate-300">
                          <td className="sticky left-0 z-10 bg-slate-100 px-2 py-2 text-center">
                            <button onClick={() => toggleGroup(group.glCode)} className="text-slate-500 hover:text-slate-700">
                              {isGroupCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="sticky left-[30px] z-10 bg-slate-100 px-3 py-2" colSpan={2}>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-slate-700 text-xs">{group.glCode}</span>
                              <span className="text-slate-500 text-[10px]">—</span>
                              <span className="font-semibold text-slate-600 text-xs truncate">{group.glName}</span>
                              {group.glType && <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-medium">{group.glType}</span>}
                              <span className="text-[10px] text-slate-400 ml-1">({lineCount} {lineCount === 1 ? 'line' : 'lines'})</span>
                            </div>
                          </td>
                          <td className="bg-slate-100 px-3 py-2 text-right font-mono font-bold text-slate-700 text-xs">
                            {formatCurrency(group.subtotals.annualBudget)}
                          </td>
                          {group.subtotals.months.map((sm, mi) => {
                            const pct = sm.budgeted > 0 ? (sm.spent / sm.budgeted) * 100 : 0;
                            const barColor = pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : pct > 0 ? 'bg-blue-500' : 'bg-gray-300';
                            return (
                              <td key={mi} className="px-2 py-2 border-l border-gray-100 bg-slate-100">
                                <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                </div>
                                <div className="flex justify-between text-[9px] mt-0.5 text-slate-500">
                                  <span>{formatCurrency(sm.spent)}</span>
                                  {pct > 0 && <span className={pct > 100 ? 'text-red-600 font-bold' : pct > 80 ? 'text-amber-600 font-semibold' : 'text-slate-400'}>{Math.round(pct)}%</span>}
                                </div>
                              </td>
                            );
                          })}
                          <td className="bg-slate-100 px-3 py-2 text-right text-xs font-bold border-l border-slate-200">
                            <span className={groupPct > 100 ? 'text-red-700' : groupPct > 80 ? 'text-amber-700' : 'text-slate-600'}>
                              {groupPct > 0 ? `${groupPct}%` : '—'}
                            </span>
                          </td>
                        </tr>
                      )}

                      {!isGroupCollapsed && group.lines.map((line, rowIdx) => {
                        const spentPct = line.annualBudget > 0
                          ? Math.round((line.spent / line.annualBudget) * 100) : 0;
                        const rowBg = rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                        return (
                          <tr key={line.budgetLineId} className={`border-b border-gray-100 hover:bg-blue-50 transition-colors group ${rowBg}`}>
                            <td className={`sticky left-0 z-10 px-2 py-2.5 group-hover:bg-blue-50 transition-colors ${rowBg}`} />
                            <td className={`sticky left-[30px] z-10 px-3 py-2.5 font-mono text-gray-400 text-[10px] group-hover:bg-blue-50 transition-colors ${rowBg}`}>
                              {showGrouping ? '' : (line.glAccountCode || '—')}
                            </td>
                            <td className={`sticky left-[110px] z-10 px-3 py-2.5 max-w-[240px] group-hover:bg-blue-50 transition-colors ${rowBg}`}>
                              <div className="truncate font-medium text-gray-900" title={line.description || line.glAccountName}>{line.description || line.glAccountName || '—'}</div>
                              {line.contract && (
                                <div className="text-[10px] text-gray-400 truncate">🔗 {line.contract.vendor} · {line.contract.frequency}</div>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono font-semibold text-gray-700 bg-gray-50/80 text-xs">
                              {formatCurrency(line.annualBudget)}
                            </td>

                            {line.months.map((m, mi) => {
                              const hasEvts = m.events.length > 0;
                              const pct = m.budgeted > 0 ? (m.spent / m.budgeted) * 100 : 0;
                              const barColor = pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : pct > 0 ? 'bg-blue-500' : 'bg-gray-200';
                              const hasAccruals = m.accrualCount > 0;

                              return (
                                <td key={mi} className="px-2 py-2 border-l border-gray-100">
                                  {/* Event icons */}
                                  {hasEvts && (
                                    <div className="flex items-center justify-center gap-0.5 mb-1">
                                      {m.events.map((ev, ei) => <EventIcon key={ei} event={ev} />)}
                                    </div>
                                  )}
                                  {/* Spend bar */}
                                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden" title={`Spent: ${formatCurrency(m.spent)} / Budget: ${formatCurrency(m.budgeted)} (${Math.round(pct)}%)`}>
                                    <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                  </div>
                                  {/* Accrual bar stacked below */}
                                  {hasAccruals && (
                                    <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden mt-0.5" title={`Accrued: ${formatCurrency(m.accrued)}`}>
                                      <div className="h-full rounded-full bg-amber-300" style={{ width: `${Math.min(m.budgeted > 0 ? (m.accrued / m.budgeted) * 100 : 0, 100)}%` }} />
                                    </div>
                                  )}
                                  {/* Labels */}
                                  <div className="flex justify-between items-baseline text-[9px] mt-0.5">
                                    <span className="text-gray-400">{m.budgeted > 0 ? formatCurrency(m.budgeted) : ''}</span>
                                    {(m.spent > 0 || m.accrued > 0) && (
                                      <span className={`font-semibold ${pct > 100 ? 'text-red-600' : pct > 80 ? 'text-amber-600' : 'text-blue-600'}`}>
                                        {m.spent > 0 ? `${Math.round(pct)}%` : <span className="text-amber-400">~{Math.round(m.budgeted > 0 ? (m.accrued / m.budgeted) * 100 : 0)}%</span>}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}

                            <td className="px-3 py-2.5 text-right text-xs font-bold border-l border-gray-200">
                              <span className={spentPct > 100 ? 'text-red-600' : spentPct > 80 ? 'text-amber-600' : spentPct > 0 ? 'text-blue-700' : 'text-gray-300'}>
                                {spentPct > 0 ? `${spentPct}%` : '—'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-20">
                <tr className="bg-gray-800 text-white">
                  <td className="sticky left-0 z-30 bg-gray-800 px-2 py-2.5" />
                  <td className="sticky left-[30px] z-30 bg-gray-800 px-3 py-2.5 font-semibold" colSpan={2}>TOTAL</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold bg-gray-700">
                    {formatCurrency(data.summary.totalAnnualBudget)}
                  </td>
                  {data.monthTotals.map((t, i) => {
                    const pct = t.budgeted > 0 ? (t.spent / t.budgeted) * 100 : 0;
                    const barColor = pct > 100 ? 'bg-red-400' : pct > 80 ? 'bg-amber-400' : 'bg-blue-400';
                    return (
                      <td key={i} className="px-2 py-2 border-l border-gray-700">
                        <div className="w-full h-2 bg-gray-600 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                        <div className="flex justify-between text-[9px] mt-0.5 text-gray-300">
                          <span>{formatCurrency(t.spent)}</span>
                          <span className={pct > 100 ? 'text-red-300 font-bold' : ''}>{pct > 0 ? `${Math.round(pct)}%` : ''}</span>
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-right font-mono font-bold border-l border-gray-700 bg-gray-700">
                    {data.summary.totalAnnualBudget > 0
                      ? `${Math.round((data.summary.totalSpent / data.summary.totalAnnualBudget) * 100)}%`
                      : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        </>
      ) : data && data.lines.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-700 mb-1">No budget lines</h3>
          <p className="text-sm text-gray-500 mb-4">This budget has no lines yet. Add budget lines first, then view the monthly spread here.</p>
          <button onClick={() => navigate(`/budget-lines?budgetId=${selectedBudgetId}`)} className="btn btn-primary btn-sm">
            Go to Budget Lines
          </button>
        </div>
      ) : !error && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Info className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-700">Select a budget</h3>
          <p className="text-sm text-gray-500">Choose a budget from the dropdown above to see the monthly spread.</p>
        </div>
      )}

      {/* Committed PO Drill-Down Modal */}
      {committedLine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Committed Purchase Orders</h2>
                <p className="text-sm text-gray-500">{committedLine.line.glAccountCode} — {committedLine.line.description}</p>
              </div>
              <button onClick={() => setCommittedLine(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4">
              {committedLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  <span className="ml-2 text-sm text-gray-500">Loading…</span>
                </div>
              ) : committedLine.pos.length === 0 && committedLine.accruals.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No committed items found for this budget line.</p>
              ) : (
                <div className="space-y-5">
                  {committedLine.pos.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Purchase Orders</h3>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                            <th className="text-left py-2 pr-3">PO</th>
                            <th className="text-left py-2 pr-3">Vendor</th>
                            <th className="text-left py-2 pr-3">Line Description</th>
                            <th className="text-left py-2 pr-3">Status</th>
                            <th className="text-right py-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {committedLine.pos.map(p => (
                            <tr key={p.POLineID} className="hover:bg-gray-50">
                              <td className="py-2 pr-3">
                                <button onClick={() => { setCommittedLine(null); navigate(`/purchase-orders/${p.POID}`); }} className="font-mono text-indigo-600 hover:underline text-xs">{p.PONumber}</button>
                                {p.PODate && <div className="text-xs text-gray-400">{new Date(p.PODate).toLocaleDateString('en-GB')}</div>}
                              </td>
                              <td className="py-2 pr-3 text-gray-700">{p.VendorName || '—'}</td>
                              <td className="py-2 pr-3 text-gray-600">{p.LineDescription || '—'}</td>
                              <td className="py-2 pr-3">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${p.POStatus === 'Approved' ? 'bg-green-100 text-green-800' : p.POStatus === 'Open' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>{p.POStatus === 'Open' ? 'Partial' : p.POStatus}</span>
                              </td>
                              <td className="py-2 text-right font-medium text-gray-900">
                                £{(p.LineTotal || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                {p.POStatus === 'Open' && p.OriginalLineTotal > p.LineTotal && (
                                  <div className="text-xs text-gray-400">of £{(p.OriginalLineTotal || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })} total</div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {committedLine.accruals.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Accruals</h3>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                            <th className="text-left py-2 pr-3">Description</th>
                            <th className="text-left py-2 pr-3">Vendor</th>
                            <th className="text-left py-2 pr-3">Date</th>
                            <th className="text-left py-2 pr-3">Status</th>
                            <th className="text-right py-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {committedLine.accruals.map(a => (
                            <tr key={a.AccrualID} className="hover:bg-gray-50">
                              <td className="py-2 pr-3 text-gray-700">{a.Description || '—'}</td>
                              <td className="py-2 pr-3 text-gray-600">{a.VendorName || '—'}</td>
                              <td className="py-2 pr-3 text-gray-500">{a.AccrualDate ? new Date(a.AccrualDate).toLocaleDateString('en-GB') : '—'}</td>
                              <td className="py-2 pr-3">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${a.Status === 'Matched' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>{a.Status}</span>
                              </td>
                              <td className="py-2 text-right font-medium text-gray-900">£{(a.EstimatedAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="border-t-2 border-gray-200 pt-2 flex justify-end">
                    <span className="text-sm font-semibold text-gray-700 mr-4">Total Committed</span>
                    <span className="font-bold text-amber-700">
                      £{(committedLine.pos.reduce((s, p) => s + (p.LineTotal || 0), 0) + committedLine.accruals.reduce((s, a) => s + (a.EstimatedAmount || 0), 0)).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Formula note ── */}
      {data && (
        <div className="mt-3 text-[11px] text-gray-400 px-1">
          Monthly allocation = Annual Budget ÷ {data.totalWeeks} weeks × weeks in month.
          {data.months.length > 0 && ` Months: ${data.months.map(m => `${m.label}(${m.weeks}w)`).join(', ')}.`}
          {' '}Click cells to select · Ctrl+click to multi-select · Shift+click for range · Esc to clear
        </div>
      )}

      {/* ── Selection status bar (Excel-style) ── */}
      {selectionSummary && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-xs px-5 py-2.5 rounded-full shadow-2xl flex items-center gap-3">
          <span className="text-gray-400">{selectionSummary.count} cell{selectionSummary.count !== 1 ? 's' : ''}</span>
          <span className="w-px h-3.5 bg-gray-600" />
          <span>Sum: <span className="font-bold">{formatCurrencyFull(selectionSummary.sum)}</span></span>
          {selectionSummary.count > 1 && (
            <>
              <span className="w-px h-3.5 bg-gray-600" />
              <span>Avg: <span className="font-bold">{formatCurrencyFull(selectionSummary.sum / selectionSummary.count)}</span></span>
            </>
          )}
          <button
            onClick={() => setSelectedCells(new Set())}
            className="ml-1 text-gray-400 hover:text-white transition-colors"
            title="Clear selection (Esc)"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Forecast Edit Modal ── */}
      {forecastEditLine && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-600" />
                <h2 className="text-base font-semibold text-gray-900">Edit Forecast</h2>
              </div>
              <button onClick={() => setForecastEditLine(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Body */}
            <div className="px-5 py-4 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 truncate" title={forecastEditLine.description || forecastEditLine.glAccountName}>
                  {forecastEditLine.description || forecastEditLine.glAccountName}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  GL {forecastEditLine.glAccountCode} · Original Budget: <span className="font-semibold">{formatCurrencyFull(forecastEditLine.annualBudget)}</span>
                  {forecastEditLine.spent > 0 && (
                    <> · Spent YTD: <span className="font-semibold text-blue-600">{formatCurrencyFull(forecastEditLine.spent)}</span></>
                  )}
                </p>
              </div>

              {/* Forecast Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  {forecastEditChangeType === 'one_time'
                    ? 'One-time Extra Payment (£)'
                    : 'New Annual Rate from Effective Date (£/year)'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={forecastEditAmount}
                  onChange={e => setForecastEditAmount(e.target.value)}
                  className="input w-full text-sm font-mono"
                  placeholder={forecastEditChangeType === 'one_time' ? 'e.g. 5000 (extra this month)' : 'e.g. 50000 (new annual value)'}
                  autoFocus
                />
                {forecastEditAmount !== '' && !isNaN(parseFloat(forecastEditAmount)) && (
                  <p className="text-xs mt-1 text-gray-500">
                    {forecastEditChangeType === 'one_time'
                      ? `One-time extra of ${formatCurrencyFull(parseFloat(forecastEditAmount))} in the effective month`
                      : (() => {
                          const newMonthly = parseFloat(forecastEditAmount) / 12;
                          const oldMonthly = forecastEditLine.annualBudget / 12;
                          const diff = newMonthly - oldMonthly;
                          return diff > 0
                            ? <span className="text-purple-600">▲ {formatCurrencyFull(diff)}/month more from effective date</span>
                            : diff < 0
                            ? <span className="text-teal-600">▼ {formatCurrencyFull(Math.abs(diff))}/month less from effective date</span>
                            : 'No change from original rate';
                        })()
                    }
                  </p>
                )}
              </div>

              {/* Effective From */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Effective From <span className="font-normal text-gray-400">(first month affected by this forecast)</span>
                </label>
                <input
                  type="date"
                  value={forecastEditEffectiveDate}
                  onChange={e => setForecastEditEffectiveDate(e.target.value)}
                  className="input w-full text-sm"
                />
                {!forecastEditEffectiveDate && (
                  <p className="text-[10px] text-gray-400 mt-0.5">If blank, defaults to today (affects current month onwards)</p>
                )}
              </div>

              {/* Change Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Change Type</label>
                <div className="flex flex-col gap-2">
                  <label className={`flex items-start gap-2.5 p-2.5 rounded border cursor-pointer transition-colors ${
                    forecastEditChangeType === 'permanent'
                      ? 'border-violet-400 bg-violet-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="changeType"
                      value="permanent"
                      checked={forecastEditChangeType === 'permanent'}
                      onChange={() => setForecastEditChangeType('permanent')}
                      className="mt-0.5 accent-violet-600"
                    />
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Permanent change</p>
                      <p className="text-[11px] text-gray-500">Enter the new annualized cost. From the effective date, each month is recalculated at the new annual rate ÷ 12. Earlier months stay at the original budget.</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2.5 p-2.5 rounded border cursor-pointer transition-colors ${
                    forecastEditChangeType === 'one_time'
                      ? 'border-violet-400 bg-violet-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="changeType"
                      value="one_time"
                      checked={forecastEditChangeType === 'one_time'}
                      onChange={() => setForecastEditChangeType('one_time')}
                      className="mt-0.5 accent-violet-600"
                    />
                    <div>
                      <p className="text-xs font-semibold text-gray-700">One-time payment</p>
                      <p className="text-[11px] text-gray-500">Only the effective month is affected. All other months keep the original budget spread.</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Reason / Notes <span className="font-normal text-gray-400">(e.g. price increase, scope change)</span>
                </label>
                <textarea
                  value={forecastEditNotes}
                  onChange={e => setForecastEditNotes(e.target.value)}
                  className="input w-full text-sm resize-none"
                  rows={3}
                  maxLength={500}
                  placeholder="Optional: explain the reason for the revised forecast"
                />
                <p className="text-[10px] text-gray-400 text-right mt-0.5">{forecastEditNotes.length}/500</p>
              </div>

              <div className="text-xs text-gray-400 bg-gray-50 rounded p-2">
                {forecastEditChangeType === 'one_time'
                  ? <><strong>One-time:</strong> Only the effective month is increased by the extra amount. All other months remain at the original budget.</>  
                  : <><strong>Permanent:</strong> Enter the new annualized cost from the effective date. E.g. contract rises from £15k to £50k/yr in November → enter 50000. Months before November stay at original rate; November onwards at £4,167/month.</>}
              </div>
            </div>
            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100">
              <button
                onClick={() => setForecastEditLine(null)}
                className="btn btn-secondary btn-sm"
                disabled={forecastSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveForecast}
                className="btn btn-primary btn-sm bg-violet-600 hover:bg-violet-700 border-violet-600"
                disabled={forecastSaving}
              >
                {forecastSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
                {forecastSaving ? 'Saving…' : 'Save Forecast'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
